import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, CardActionArea, CardContent, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../components/context/darkModeContext";
import { isMobile } from "../../globalStyles/mobile";
import { DASHBOARD_PKEY, DASHBOARD_TILES } from "./catalog";

const PAGE_FONT = "Poppins";

const readDashboardChildren = () => {
  try {
    const items = JSON.parse(localStorage.getItem("sidemenuItems") || "[]");
    const dashboard = items.find((item) => item.pkey === DASHBOARD_PKEY);
    return Array.isArray(dashboard?.subNav) ? dashboard.subNav : [];
  } catch {
    return [];
  }
};

const TileBody = ({ tile, primaryColor, colors }) => {
  const Icon = tile.Icon;
  return (
    <CardContent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        p: "22px 22px 24px !important",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          backgroundColor: `${primaryColor}14`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: primaryColor,
          mb: 1.75,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Typography
        sx={{
          fontSize: 14.5,
          fontWeight: 600,
          color: colors.textPrimary,
          fontFamily: PAGE_FONT,
          letterSpacing: "-0.01em",
          lineHeight: 1.3,
          mb: 0.75,
        }}
      >
        {tile.title}
      </Typography>
      {tile.description ? (
        <Typography
          sx={{
            fontSize: 13,
            color: colors.textSecondary,
            lineHeight: 1.55,
            fontFamily: PAGE_FONT,
            fontWeight: 400,
          }}
        >
          {tile.description}
        </Typography>
      ) : null}
    </CardContent>
  );
};

const DashboardLandingPage = () => {
  const { darkMode, colors } = useDarkMode();
  const theme = useTheme();
  const navigate = useNavigate();
  const mobile = isMobile();
  const primaryColor = theme?.palette?.primary?.main || colors.accent;
  const [children, setChildren] = useState(readDashboardChildren);

  useEffect(() => {
    const reload = () => setChildren(readDashboardChildren());
    window.addEventListener("sidemenu-updated", reload);
    return () => window.removeEventListener("sidemenu-updated", reload);
  }, []);

  const navByPkey = useMemo(() => {
    const map = {};
    children.forEach((item) => {
      if (item?.pkey) map[item.pkey] = item;
    });
    return map;
  }, [children]);

  const tiles = useMemo(
    () =>
      DASHBOARD_TILES.map((tile) => {
        const nav = navByPkey[tile.pkey];
        const path = nav?.path || tile.path || null;
        const canOpen = Boolean(path) && nav?.accessible !== false;
        return {
          ...tile,
          title: nav?.title || tile.title,
          path: canOpen ? path : null,
        };
      }),
    [navByPkey],
  );

  const cardBorder = darkMode ? colors.border : "var(--border-color-subtle)";
  const cardSx = {
    backgroundColor: colors.bgPaper,
    borderRadius: "10px",
    border: `1px solid ${cardBorder}`,
    boxShadow: "none",
    minHeight: mobile ? 0 : 168,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <Box
      sx={{
        backgroundColor: darkMode ? colors.bgPrimary : "var(--dashboard-bg)",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: mobile ? "8px 12px 32px" : "8px 28px 40px",
        }}
      >
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: mobile ? 20 : 22,
              fontWeight: 600,
              color: "var(--text-heading)",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Insight
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)",
            gridAutoRows: mobile ? "auto" : "1fr",
            gap: mobile ? 1.5 : 2.25,
          }}
        >
          {tiles.map((tile) => {
            const clickable = Boolean(tile.path);
            return (
              <Card
                key={tile.pkey}
                elevation={0}
                sx={{
                  ...cardSx,
                  ...(clickable
                    ? {
                        cursor: "pointer",
                        transition:
                          "border-color 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": {
                          borderColor: darkMode
                            ? colors.borderLight
                            : "var(--border-hover)",
                          boxShadow: darkMode
                            ? "0 1px 2px rgba(0,0,0,0.25)"
                            : "0 1px 2px rgba(16,24,40,0.06)",
                        },
                      }
                    : {
                        cursor: "default",
                      }),
                }}
              >
                {clickable ? (
                  <CardActionArea
                    disableRipple
                    onClick={() => navigate(tile.path)}
                    sx={{
                      height: "100%",
                      alignItems: "stretch",
                      ".MuiCardActionArea-focusHighlight": { opacity: 0 },
                    }}
                  >
                    <TileBody
                      tile={tile}
                      primaryColor={primaryColor}
                      colors={colors}
                    />
                  </CardActionArea>
                ) : (
                  <Box sx={{ height: "100%", pointerEvents: "none" }}>
                    <TileBody
                      tile={tile}
                      primaryColor={primaryColor}
                      colors={colors}
                    />
                  </Box>
                )}
              </Card>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLandingPage;
